import { useCallback, useMemo } from 'react'
import { type CustomEventParams, type GenerateRequestTokenParams } from '@incognia/web-sdk'
import { INCOGNIA_APP_ID } from 'shared/envs'

import { deviceFingerPrintEvents } from '../analitica'
import { useDeviceFingerPrintStore } from '../store/device-finger-print'

interface UseDeviceFingerPrint {
  isLoading: boolean
  error?: string
  initDeviceFingerPrint: () => Promise<void>
  setAccountId: (accountId: string) => Promise<void>
  clearAccountId: () => Promise<void>
  generateRequestToken: (options?: GenerateRequestTokenParams) => Promise<string | undefined>
  sendCustomEvent: (params: CustomEventParams) => Promise<void>
}

export const disabledDeviceFingerPrint: UseDeviceFingerPrint = {
  isLoading: false,
  error: undefined,
  initDeviceFingerPrint: async () => {},
  setAccountId: async () => {},
  clearAccountId: async () => {},
  generateRequestToken: async () => undefined,
  sendCustomEvent: async () => {}
}

export const useDeviceFingerPrint = (): UseDeviceFingerPrint => {
  const { isLoading, error, setValues, sdkInstance } = useDeviceFingerPrintStore()

  const initDeviceFingerPrint = useCallback(async (): Promise<void> => {
    try {
      if (process.env.NODE_ENV === 'development') {
        return await sdkInstance.init({ appId: INCOGNIA_APP_ID(), logEnabled: true, trackUserInteraction: true })
      } else {
        return await sdkInstance.init(INCOGNIA_APP_ID())
      }
    } catch (error: unknown) {
      const messageError = error instanceof Error ? error.message : 'Erro de inicialização da SDK'
      deviceFingerPrintEvents.events.flowLogin.error(messageError)
      setValues({
        isLoading: false,
        error: messageError
      })
    }
  }, [sdkInstance, setValues])

  const setAccountId = useCallback(
    async (accountId: string): Promise<void> => {
      deviceFingerPrintEvents.events.home.setAccountId()
      await sdkInstance?.setAccountId(accountId)
    },
    [sdkInstance]
  )

  const clearAccountId = useCallback(async (): Promise<void> => {
    await sdkInstance?.clearAccountId()
  }, [sdkInstance])

  const generateRequestToken = useCallback(
    async (options?: GenerateRequestTokenParams): Promise<string | undefined> => {
      deviceFingerPrintEvents.events.flowLogin.generateToken()
      const token = await sdkInstance?.generateRequestToken(options)
      if (!token) {
        deviceFingerPrintEvents.events.flowLogin.error('Erro ao gerar o token da Incognia')
      }
      return token
    },
    [sdkInstance]
  )

  const sendCustomEvent = useCallback(
    async (params: CustomEventParams): Promise<void> => {
      await sdkInstance?.sendCustomEvent(params)
    },
    [sdkInstance]
  )

  const enableApi = useMemo(
    () => ({
      isLoading,
      error,
      initDeviceFingerPrint,
      setAccountId,
      clearAccountId,
      generateRequestToken,
      sendCustomEvent
    }),
    [clearAccountId, error, generateRequestToken, initDeviceFingerPrint, isLoading, sendCustomEvent, setAccountId]
  )

  if (!sdkInstance) {
    return disabledDeviceFingerPrint
  }

  return enableApi
}
